import { CanvasScene } from "../scenes/Board/CanvasScene";
import { Board } from "../scenes/Board/Board";
import { MyPoint } from "../../typescript/MyPoint";
import { CanvasManager } from "../scenes/Board/CanvasManager";
import { GameVars } from "../GameVars";
import { GuiScene } from "../scenes/GUI/GuiScene";


export class CameraManager {

    public isDragging = false;
    public hasDragged = false;
    public multiTouch = false;
    public dragToPaint: boolean = false;
    public hasDragToPaint: boolean = false;
    public dragTimerLimit: number;
    public zoom: number;
    public ignoreTab: boolean = false;
    public cam: Phaser.Cameras.Scene2D.Camera;

    private scene: CanvasScene;
    private board: Board;
    private enableMovement: boolean;
    private holdingDown = false;
    private dragTimer: number;
    private dragThreshold: number;
    private allowDrag: boolean;
    private zoomStep: number;
    private maxZoom: number;
    private minZoom: number;
    private currentZoom: number = 0;
    private zoomRange: number = 0;
    private initPanPoint = new MyPoint(0, 0);
    private oldPoint = new MyPoint(0, 0);
    private oldPinchDistance: number = 0;
    private cameraPadding: number = 80;
    private stopAdding: boolean = false;
    private isVertical: boolean = false;

    constructor(mainCam: Phaser.Cameras.Scene2D.Camera, boardInstance: CanvasScene, enables: boolean, board: Board) {

        this.cam = mainCam;
        this.scene = boardInstance;

        this.enableMovement = enables;
        this.stopAdding = false;
        this.multiTouch = false;

        this.board = board;

        this.maxZoom = 2;

        this.isDragging = false;
        this.holdingDown = false;
        this.zoomStep = 0.05;
        this.zoom = 1;
        this.currentZoom = 0;

        this.cameraPadding = 1100 * GameVars.scaleXMultInverse;

        this.dragToPaint = false;
        this.dragTimer = 0;
        this.dragTimerLimit = 350;
        this.allowDrag = false;
        this.dragThreshold = 5;

        this.initPanPoint.set(0, 0);
        this.oldPoint.set(0, 0);
        this.oldPinchDistance = 0;

        this.createCallbacks();
        this.listenScrollWheel();

        this.cam.setPosition(0, -GameVars.paletteHeight / 2);

        // Zoom in or Zoom out the image to fit the screen
        this.zoom = this.cam.displayWidth / (CanvasManager.widthInPixels + this.cameraPadding / 2) * GameVars.scaleXMultInverse;
     
        this.minZoom = this.zoom;

        this.zoomRange = this.maxZoom - this.minZoom;

        this.cam.setZoom(this.zoom);
        this.checkZoomLimits();

        // Set Camera Boundaries
        this.cam.setBounds(this.cam.centerX - this.cam.displayWidth / 2, this.cam.centerY - this.cam.displayHeight / 2 + (GameVars.paletteHeight / 2), this.cam.displayWidth, this.cam.displayHeight);

        // Add another pointer to create pinch controlls 
        this.scene.input.addPointer(1);
    }

    public update(delta: number): void {

        // If user is clicking add to the drag timer 
        if (this.holdingDown) {
            this.dragTimer += delta;
        }

        if (!this.isPointer1Down() || !this.isPointer2Down()) {
            if (this.dragTimer >= this.dragTimerLimit) {

                if (this.isDragging) {
                    GuiScene.currentInstance.pencilImage.visible = false;
                }
            }
        }

        this.updatePinch();
    }

    public isPointer1Down(): boolean {

        return this.scene.input.pointer1.isDown;
    }

    public isPointer2Down(): boolean {

        return this.scene.input.pointer2.isDown;
    }

    public checkZoomLimits(): boolean {

        let ret: boolean = true;
        // Get the point where the camera should stop zooming out 
        if (!this.isVertical) {
            if ((CanvasManager.widthInPixels + this.cameraPadding / 2) * GameVars.scaleXMult <= this.cam.displayWidth) {
                this.stopAdding = true;
                ret = false;
                this.cam.setZoom(this.minZoom);
            } else {
                this.stopAdding = false;
            }
        } else {

            if ((CanvasManager.heightInPixels + this.cameraPadding) * GameVars.scaleYMult <= this.cam.displayHeight) {
                this.stopAdding = true;
                ret = false;
                this.cam.setZoom(this.minZoom);
            } else {
                this.stopAdding = false;
            }
        }

        return ret;
    }

    public isPaintingWithDrag(): boolean {

        return this.dragToPaint;
    }

    public updatePinch(): void {

        // Check pinch input 
        if (this.isPointer1Down() && this.isPointer2Down()) {

            if (!this.multiTouch) {
                this.oldPinchDistance = this.getDistanceBetweenPointers();
            }

            this.multiTouch = true;

        } else {
            this.multiTouch = false;
        }

        // We have two pointers: pointer1 & pointer2 from which we can get a position.
        // If the user is making bigger or smaller the distance, it will zoom in or out.
        if (this.multiTouch) {

            let newDistance = this.getDistanceBetweenPointers();
            let distanceFromPreviousFrame = newDistance - this.oldPinchDistance;

            if (distanceFromPreviousFrame < 0) {
                distanceFromPreviousFrame *= -1;
            }

            const zoomInterval = 0.025 * CanvasManager.widthInPixels / 1000 * distanceFromPreviousFrame / 5;

            if (newDistance > this.oldPinchDistance && distanceFromPreviousFrame > 1) {

                this.zoom += zoomInterval;
                this.currentZoom += 0.025 * distanceFromPreviousFrame / 5;

                if (this.zoom > this.maxZoom) {
                    this.zoom = this.maxZoom;
                    this.currentZoom = this.zoomRange;
                }

                this.cam.setZoom(this.zoom);
                this.checkZoomLimits();

            } else if (this.stopAdding === false && newDistance < this.oldPinchDistance && distanceFromPreviousFrame > 1) {

                if ((this.zoom - zoomInterval) <= 0) {
                    return;
                }

                this.zoom -= zoomInterval;
                this.currentZoom -= 0.025 * distanceFromPreviousFrame / 5;

                this.cam.setZoom(this.zoom);
                this.checkZoomLimits();
            }

            this.oldPinchDistance = newDistance;
        }
    }

    public getZoomNormalized(): number {

        let ret = this.currentZoom / this.zoomRange;

        if (ret < 0) {
            ret = 0;
        }

        return ret;
    }

    public setMinZoom(): void {

        this.zoom = this.minZoom;
        this.cam.zoomTo(this.zoom, 1000);
    }

    public getDistanceBetweenPointers(): number {

        return Phaser.Math.Distance.Between(this.scene.input.pointer1.x, this.scene.input.pointer1.y, this.scene.input.pointer2.x, this.scene.input.pointer2.y);
    }
   
    public onPointerUp(): void {

        this.holdingDown = false;
        this.dragTimer = 0;

        if (this.isDragging) {
            this.hasDragged = true;
        } else {
            this.hasDragged = false;
        }

        this.isDragging = false;
    }

    private listenScrollWheel(): void {

        if (!this.enableMovement) {
            return;
        }

        // Check for wheel zooming 
        document.addEventListener("wheel", (event) => {

            if (event.deltaY !== 0) {
                if (event.deltaY < 0) {

                    this.zoom += this.zoomStep;
                    this.currentZoom += this.zoomStep;

                } else if (!this.stopAdding) {

                    this.zoom -= this.zoomStep;
                    this.currentZoom -= this.zoomStep;


                }

                // Set some constrains to it          
                if (this.zoom > this.maxZoom) {
                    this.zoom = this.maxZoom;
                    this.currentZoom = this.zoomRange;
                }

                this.cam.setZoom(this.zoom);
                this.checkZoomLimits();
            }
        });
    }

    private createCallbacks(): void {

        if (!this.enableMovement) {
            return;
        }

        this.scene.input.on("pointerdown", function (pointer: Phaser.Geom.Point): void {

            this.holdingDown = true;

            this.isDragging = false;
            this.hasDragged = false;
            this.dragToPaint = false;

            this.oldPoint.set(pointer.x, pointer.y);
            this.initPanPoint.set(pointer.x, pointer.y);

        }, this);

        this.scene.input.on("pointermove", function (pointer: Phaser.Geom.Point): void {

            if (!this.holdingDown || this.multiTouch) {
                return;
            }

            // Threshold to start dragging with a minimum distance, done to use finger in mobile
            if (Phaser.Math.Distance.Between(this.initPanPoint.x, this.initPanPoint.y, pointer.x, pointer.y) < this.dragThreshold) {
                return;
            }

            // This will filter cases in where multitouch is confused with a huge drag 
            if (Phaser.Math.Distance.Between(this.oldPoint.x, this.oldPoint.y, pointer.x, pointer.y) > 150) {
                return;
            }

            // If the user pressed the button and waited more than 500 ms, map will not drag.           
            if (this.dragTimer >= this.dragTimerLimit) {

                this.dragToPaint = true;

            } else {
                this.isDragging = true;
                this.hasDragged = true;
            }

            if (this.isDragging && !this.multiTouch) {

                // Get the movement ammount of the last frame 
                let incX: number;
                let incY: number;

                incX = pointer.x - this.oldPoint.x;
                incY = pointer.y - this.oldPoint.y;

                // Apply it 
                this.cam.scrollX -= (incX / this.zoom);
                this.cam.scrollY -= (incY / this.zoom);
            }

            this.oldPoint.set(pointer.x, pointer.y);

        }, this);

        this.scene.input.on("pointerup", function (): void {

            CanvasScene.currentInstance.unclickPointer();

        }, this);
    }
}
